/**
 * Site audits: every public audit read as one desk. The Sissetulevad tab of
 * Kliendiotsing, the first step of the funnel: the sites whose owners came to
 * us.
 *
 * The tab exists because no audit answers the question on its own. Each one
 * tells a stranger about their own site; together they tell US which sites
 * anyone has looked at and how thoroughly - and the gaps are the work. A domain
 * with an agent-readiness score and no compliance score is a prospect who
 * already gave us an address and part of a diagnosis, which is the cheapest
 * call on the list.
 *
 * Coverage is the only filter that needed thinking about. It is a statement
 * about the whole SET of audits a domain has, so the server applies it after
 * folding the rows, which means a filtered page can come back shorter than it
 * asked for. That is said out loud under the table rather than left as a
 * mystery - a list that silently returns fewer rows than the page size reads as
 * the end of the data.
 *
 * Everything per-audit is walked from `AUDIT_KINDS` and looked up in
 * `auditKinds.ts`. The tiles, the columns, the filter chips and the history rows
 * all used to name the two audits by hand, in four files.
 *
 * No head of its own: the page's head (`prospecting/ProspectingSection.tsx`)
 * carries the title and this tab's sentence, and the tab starts at its numbers.
 */
import { useState } from "react";
import { RotateCcw, ShieldCheck, Sparkles, Users } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import { Btn, Kpi, SearchField, ToggleChip, Toolbar, ToolbarGap, fmtNum } from "../../ui";
import { AUDIT_KINDS, type AuditCoverage, type AuditKind, type AuditStatus } from "../../../lib/siteAudits";
import { AUDIT_KIND_META } from "./auditKinds";
import { AuditsTable } from "./AuditsTable";
import { AuditHistoryDialog } from "./AuditHistoryDialog";
import { useSiteAudits } from "./useSiteAudits";

/** "all", then one "has not had this audit yet" chip per kind, then "complete". */
const COVERAGES: AuditCoverage[] = [
  "all",
  ...AUDIT_KINDS.map((k) => `missing-${k}` as AuditCoverage),
  "complete",
];

export function SiteAuditsSection(_props: { user: StaffUser }) {
  const t = useT();
  const desk = useSiteAudits();
  const [openDomain, setOpenDomain] = useState<string | null>(null);

  const coverageLabel = (c: AuditCoverage): string => {
    if (c === "all") return t("siteAudits.coverage.all");
    if (c === "complete") return t("siteAudits.coverage.complete");
    return t(AUDIT_KIND_META[c.slice("missing-".length) as AuditKind].missingKey);
  };

  /** Column and row labels per audit, resolved once for the two children. */
  const kindLabels = Object.fromEntries(
    AUDIT_KINDS.map((k) => [k, t(AUDIT_KIND_META[k].labelKey)]),
  ) as Record<AuditKind, string>;

  return (
    // A fragment, not a second `.crm-page`: the layout already wraps every
    // route in one, and a nested one replayed the entrance on this tab alone.
    <>
      {desk.totals ? (
        <div className="crm-kpis">
          <Kpi icon={ShieldCheck} label={t("siteAudits.kpi.domains")} value={fmtNum(desk.totals.domains)} />
          {AUDIT_KINDS.map((kind) => (
            <Kpi
              key={kind}
              icon={AUDIT_KIND_META[kind].icon}
              label={t(AUDIT_KIND_META[kind].totalKey)}
              value={fmtNum(desk.totals!.byKind[kind] ?? 0)}
              tone="primary"
            />
          ))}
          <Kpi
            icon={Sparkles}
            label={t("siteAudits.kpi.complete")}
            value={fmtNum(desk.totals.complete)}
            tone="success"
            foot={t("siteAudits.kpi.completeFoot")}
          />
          <Kpi icon={Users} label={t("siteAudits.kpi.leads")} value={fmtNum(desk.totals.leads)} tone="success" />
        </div>
      ) : null}

      <Toolbar>
        <SearchField
          value={desk.filters.q}
          onChange={(v) => desk.setFilter("q", v)}
          placeholder={t("siteAudits.searchPlaceholder")}
        />
        {COVERAGES.map((c) => (
          <ToggleChip key={c} active={desk.filters.coverage === c} onClick={() => desk.setFilter("coverage", c)}>
            {coverageLabel(c)}
          </ToggleChip>
        ))}
        <ToggleChip
          active={desk.filters.status === "leads"}
          onClick={() =>
            desk.setFilter("status", (desk.filters.status === "leads" ? "all" : "leads") as AuditStatus)
          }
        >
          {t("siteAudits.onlyLeads")}
        </ToggleChip>
        <ToolbarGap />
        {desk.filtered ? (
          <Btn tone="plain" small onClick={desk.reset}>
            <RotateCcw className="size-[15px]" strokeWidth={1.8} />
            {t("siteAudits.reset")}
          </Btn>
        ) : null}
      </Toolbar>

      {desk.error ? (
        <div className="crm-card py-10 text-center text-[13px] text-[var(--color-destructive)]">
          {t("siteAudits.failed")}
        </div>
      ) : (
        <AuditsTable
          rows={desk.rows}
          loading={desk.loading}
          onOpen={setOpenDomain}
          labels={{
            site: t("siteAudits.col.site"),
            kinds: kindLabels,
            contact: t("siteAudits.col.contact"),
            lastSeen: t("siteAudits.col.lastSeen"),
            notAudited: t("siteAudits.notAudited"),
            issues: t("siteAudits.issues"),
            runs: t("siteAudits.runs"),
            openReport: t("siteAudits.openReport"),
            emptyTitle: t("siteAudits.empty.title"),
            emptyBody: t("siteAudits.empty.body"),
            noContact: t("siteAudits.noContact"),
          }}
        />
      )}

      {/* No silent caps: when the coverage filter shortened this page, say so
          rather than letting a short page read as the end of the list. */}
      {desk.filteredOut > 0 ? (
        <p className="crm-mut px-1 pt-2 text-[12px]">
          {t("siteAudits.filteredOut", { n: desk.filteredOut })}
        </p>
      ) : null}

      {desk.page > 0 || desk.hasMore ? (
        <div className="flex items-center justify-center gap-2 pt-3">
          <Btn tone="plain" small disabled={desk.page === 0} onClick={() => desk.setPage(desk.page - 1)}>
            {t("common.prev")}
          </Btn>
          <span className="crm-mut crm-tnum text-[12.5px]">{desk.page + 1}</span>
          <Btn tone="plain" small disabled={!desk.hasMore} onClick={() => desk.setPage(desk.page + 1)}>
            {t("common.next")}
          </Btn>
        </div>
      ) : null}

      {openDomain ? (
        <AuditHistoryDialog
          domain={openDomain}
          onClose={() => setOpenDomain(null)}
          labels={{
            title: t("siteAudits.history.title"),
            kinds: kindLabels,
            openReport: t("siteAudits.openReport"),
            opened: t("siteAudits.history.opened"),
            campaign: t("siteAudits.history.campaign"),
            gaveAddress: t("siteAudits.history.gaveAddress"),
            noAddress: t("siteAudits.history.noAddress"),
            loading: t("common.loading"),
            failed: t("siteAudits.failed"),
          }}
        />
      ) : null}
    </>
  );
}
